import { useEffect, useState } from 'react';
import { CONTACT_EVENT_TYPES, SUB_STATUSES, TIERS, TIER_DISPLAY_NAME, type Rule, type SegmentRules } from '@/types';
import { api } from '@/lib/api';

type Group = 'all' | 'any' | 'none';

interface FieldDef {
  field: Rule['field'];
  label: string;
  group: string;
  ops: { value: string; label: string }[];
  value: 'tiers' | 'statuses' | 'days' | 'number' | 'pct' | 'text' | 'boolean' | 'feature' | 'orgRole' | 'source' | 'event' | 'template' | 'domains' | 'none';
}

const NUM_OPS = [
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'at most' },
  { value: 'gt', label: 'more than' },
  { value: 'gte', label: 'at least' },
  { value: 'eq', label: 'exactly' },
];
const FIELDS: FieldDef[] = [
  { field: 'tier', label: 'Plan', group: 'Plan & billing', ops: [{ value: 'in', label: 'is one of' }, { value: 'not_in', label: 'is not' }], value: 'tiers' },
  { field: 'subStatus', label: 'Subscription status', group: 'Plan & billing', ops: [{ value: 'in', label: 'is one of' }, { value: 'not_in', label: 'is not' }], value: 'statuses' },
  { field: 'cancelAtPeriodEnd', label: 'Set to cancel at period end', group: 'Plan & billing', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'currentPeriodEndsInDays', label: 'Billing period ends in (days)', group: 'Plan & billing', ops: NUM_OPS, value: 'number' },
  { field: 'creditsUsedPct', label: 'Monthly credits used (%)', group: 'Plan & billing', ops: NUM_OPS, value: 'pct' },
  { field: 'signedUpDaysAgo', label: 'Signed up (days ago)', group: 'Activity', ops: NUM_OPS, value: 'days' },
  { field: 'lastActivityDaysAgo', label: 'Last activity (days ago)', group: 'Activity', ops: NUM_OPS, value: 'days' },
  { field: 'usage30d', label: 'Usage in last 30 days', group: 'Activity', ops: NUM_OPS, value: 'feature' },
  { field: 'event', label: 'Had event', group: 'Activity', ops: [{ value: 'occurred_within_days', label: 'in the last' }, { value: 'not_occurred_within_days', label: 'NOT in the last' }], value: 'event' },
  { field: 'receivedTemplate', label: 'Received template', group: 'Email', ops: [{ value: 'never', label: 'never' }, { value: 'within_days', label: 'in the last' }], value: 'template' },
  { field: 'suppressed', label: 'Unsubscribed / suppressed', group: 'Email', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'profileCompleted', label: 'Profile completed', group: 'Account', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'emailVerified', label: 'Email verified', group: 'Account', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'isApproved', label: 'Approved', group: 'Account', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'isActive', label: 'Account active', group: 'Account', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'hasOrg', label: 'Belongs to an organization', group: 'Organization', ops: [{ value: 'eq', label: 'is' }], value: 'boolean' },
  { field: 'orgRole', label: 'Organization role', group: 'Organization', ops: [{ value: 'eq', label: 'is' }], value: 'orgRole' },
  { field: 'emailDomain', label: 'Email domain', group: 'Profile', ops: [{ value: 'in', label: 'is one of' }, { value: 'not_in', label: 'is not' }], value: 'domains' },
  { field: 'userFunction', label: 'Job function', group: 'Profile', ops: [{ value: 'eq', label: 'is' }], value: 'text' },
  { field: 'source', label: 'Source', group: 'Profile', ops: [{ value: 'eq', label: 'is' }], value: 'source' },
  { field: 'tags', label: 'Tag', group: 'CRM', ops: [{ value: 'contains', label: 'has' }, { value: 'not_contains', label: 'does not have' }], value: 'text' },
];
const FEATURES = ['design_studio_pro', 'stone_finder', 'jewelry_builder', 'pricing_assistant', 'images', 'images:design-studio', 'images:image-sandbox', 'conversations', 'technical_report_compose', 'module_create-cad'];

function defaultRule(field: Rule['field']): Rule {
  const def = FIELDS.find((f) => f.field === field)!;
  const op = def.ops[0].value;
  switch (def.value) {
    case 'tiers':
      return { field: 'tier', op: op as 'in', value: ['free'] };
    case 'statuses':
      return { field: 'subStatus', op: op as 'in', value: ['active'] };
    case 'boolean':
      return { field, op: 'eq', value: true } as Rule;
    case 'feature':
      return { field: 'usage30d', feature: 'design_studio_pro', op: 'gt', value: 0 };
    case 'orgRole':
      return { field: 'orgRole', op: 'eq', value: 'admin' };
    case 'source':
      return { field: 'source', op: 'eq', value: 'facetflow' };
    case 'domains':
      return { field: 'emailDomain', op: 'in', value: ['gmail.com'] };
    case 'event':
      return { field: 'event', op: 'occurred_within_days', type: 'upgrade', value: 30 };
    case 'template':
      return { field: 'receivedTemplate', op: 'never', templateId: '' };
    case 'text':
      return { field, op: op as never, value: '' } as Rule;
    default:
      return { field, op: op as 'lt', value: def.value === 'pct' ? 80 : 30 } as Rule;
  }
}

function RuleRow({ rule, onChange, onRemove, templates }: { rule: Rule; onChange: (r: Rule) => void; onRemove: () => void; templates: { id: string; name: string }[] }) {
  const def = FIELDS.find((f) => f.field === rule.field)!;
  const r = rule as Record<string, unknown>;
  const set = (patch: Record<string, unknown>) => onChange({ ...rule, ...patch } as Rule);
  const sel = 'h-9 rounded-md border px-2 text-sm bg-white';
  const inp = 'h-9 rounded-md border px-2 text-sm w-24';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-white p-2">
      <select className={sel} value={rule.field} onChange={(e) => onChange(defaultRule(e.target.value as Rule['field']))}>
        {[...new Set(FIELDS.map((f) => f.group))].map((g) => (
          <optgroup key={g} label={g}>
            {FIELDS.filter((f) => f.group === g).map((f) => (
              <option key={f.field} value={f.field}>
                {f.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {def.value === 'feature' && (
        <input className="h-9 w-48 rounded-md border px-2 text-sm" list="ff-features" value={String(r.feature ?? '')} onChange={(e) => set({ feature: e.target.value })} placeholder="feature key" />
      )}
      {def.value === 'event' && (
        <select className={sel} value={String(r.type)} onChange={(e) => set({ type: e.target.value })}>
          {CONTACT_EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}
      {def.value === 'template' && (
        <select className={sel} value={String(r.templateId ?? '')} onChange={(e) => set({ templateId: e.target.value })}>
          <option value="">— template —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      )}
      <select className={sel} value={rule.op} onChange={(e) => set({ op: e.target.value })}>
        {def.ops.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {def.value === 'tiers' && (
        <span className="flex flex-wrap gap-1">
          {TIERS.map((t) => {
            const selected = (r.value as string[]).includes(t);
            return (
              <button
                type="button"
                key={t}
                className={`rounded px-2 py-1 text-xs ring-1 ${selected ? 'bg-[var(--ff-primary)] text-white ring-[var(--ff-primary)]' : 'bg-white ring-border'}`}
                onClick={() => set({ value: selected ? (r.value as string[]).filter((v) => v !== t) : [...(r.value as string[]), t] })}
              >
                {TIER_DISPLAY_NAME[t]}
              </button>
            );
          })}
        </span>
      )}
      {def.value === 'statuses' && (
        <span className="flex flex-wrap gap-1">
          {SUB_STATUSES.map((s) => {
            const selected = (r.value as string[]).includes(s);
            return (
              <button
                type="button"
                key={s}
                className={`rounded px-2 py-1 text-xs ring-1 ${selected ? 'bg-[var(--ff-primary)] text-white ring-[var(--ff-primary)]' : 'bg-white ring-border'}`}
                onClick={() => set({ value: selected ? (r.value as string[]).filter((v) => v !== s) : [...(r.value as string[]), s] })}
              >
                {s}
              </button>
            );
          })}
        </span>
      )}
      {(def.value === 'days' || def.value === 'number' || def.value === 'pct' || def.value === 'feature' || def.value === 'event' || (def.value === 'template' && rule.op === 'within_days')) && (
        <span className="flex items-center gap-1 text-sm">
          <input type="number" className={inp} value={Number(r.value ?? 0)} onChange={(e) => set({ value: Number(e.target.value) })} />
          {(def.value === 'days' || def.value === 'event' || def.value === 'template') && <span className="text-muted-foreground">days</span>}
          {def.value === 'pct' && <span className="text-muted-foreground">%</span>}
          {def.value === 'feature' && <span className="text-muted-foreground">times</span>}
        </span>
      )}
      {def.value === 'boolean' && (
        <select className={sel} value={String(r.value)} onChange={(e) => set({ value: e.target.value === 'true' })}>
          <option value="true">yes</option>
          <option value="false">no</option>
        </select>
      )}
      {def.value === 'orgRole' && (
        <select className={sel} value={String(r.value)} onChange={(e) => set({ value: e.target.value })}>
          <option value="admin">admin</option>
          <option value="member">member</option>
        </select>
      )}
      {def.value === 'source' && (
        <select className={sel} value={String(r.value)} onChange={(e) => set({ value: e.target.value })}>
          <option value="facetflow">Facet Flow user</option>
          <option value="import">Imported lead</option>
          <option value="manual">Manual</option>
        </select>
      )}
      {def.value === 'text' && <input className="h-9 w-40 rounded-md border px-2 text-sm" value={String(r.value ?? '')} onChange={(e) => set({ value: e.target.value })} />}
      {def.value === 'domains' && (
        <input
          className="h-9 w-64 rounded-md border px-2 text-sm"
          defaultValue={(r.value as string[]).join(', ')}
          placeholder="gmail.com, yahoo.com"
          onBlur={(e) => set({ value: e.target.value.split(/[,\s]+/).map((d) => d.trim().replace(/^@/, '').toLowerCase()).filter(Boolean) })}
        />
      )}
      <button type="button" onClick={onRemove} className="ml-auto text-xs text-muted-foreground hover:text-red-600">
        remove
      </button>
    </div>
  );
}

const GROUP_LABELS: Record<Group, { title: string; hint: string }> = {
  all: { title: 'Match ALL of', hint: 'every rule must be true' },
  any: { title: 'and ANY of', hint: 'at least one must be true' },
  none: { title: 'and NONE of', hint: 'exclude anyone matching' },
};

export function RuleBuilder({ value, onChange }: { value: SegmentRules; onChange: (rules: SegmentRules) => void }) {
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    api<{ id: string; name: string }[]>('/templates').then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const groups: Group[] = ['all', 'any', 'none'];
  const update = (group: Group, rules: Rule[]) => onChange({ ...value, [group]: rules });

  return (
    <div className="space-y-3">
      <datalist id="ff-features">
        {FEATURES.map((f) => (
          <option key={f} value={f} />
        ))}
      </datalist>
      {groups.map((group) => {
        const rules = (value[group] ?? []) as Rule[];
        return (
          <div key={group} className="space-y-2 rounded-lg border bg-[var(--ff-surface-secondary)] p-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">{GROUP_LABELS[group].title}</span>
              <span className="text-xs text-muted-foreground">{GROUP_LABELS[group].hint}</span>
            </div>
            {rules.map((rule, i) => (
              <RuleRow
                key={i}
                rule={rule}
                templates={templates}
                onChange={(r) => update(group, rules.map((x, j) => (j === i ? r : x)))}
                onRemove={() => update(group, rules.filter((_, j) => j !== i))}
              />
            ))}
            <button type="button" className="ff-link text-xs" onClick={() => update(group, [...rules, defaultRule('tier')])}>
              + add rule
            </button>
          </div>
        );
      })}
    </div>
  );
}
